/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useState, useCallback } from 'react';
import {
  Button, Grid, TextField, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell,
  MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Box, IconButton,
  CircularProgress,
} from '@mui/material';
import { Note, Download, UploadFile, CheckCircle, Cancel, Edit } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UserContext } from '../../../../components/shared/useUser';
import { client, CreditRequestItemModel, SaveCreditRequestItemModel } from '../../../../app/services/api/orderManagementClient';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import PageTitle from '../../../../components/shared/PageTitle';
import PageArea from '../../../../components/shared/PageArea';
import BackButton from '../../../../components/shared/actions/BackButton';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import TextWithOverflowTooltip from '../../../../components/shared/TextWithOverflowTooltip';
import { useTranslation } from 'react-i18next';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { getCreditRequestValue, UOM_OPTIONS } from '../helpers/DeliveryHelpers';
import { CreditRequestReasonEnum } from '../enums/DeliveryEnums';
import OrderStatusChip from '../../../../components/shared/orders/OrderStatusChip';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CustomPagination from '../../../../components/forms/Pagination';
import { applyRoundAndFormat } from '../../../../utils';

interface CreditRequestEntry {
  quantity: number;
  uom: string;
  reason: CreditRequestReasonEnum;
  files: File[];
  notes: string;
}

const CreditRequestsOfd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { t } = useTranslation('orderHistory');
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [deliveryItems, setDeliveryItems] = useState<CreditRequestItemModel[]>([]);
  const [creditEntries, setCreditEntries] = useState<{ [key: number]: CreditRequestEntry }>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' }>({ open: false, message: '', severity: 'info' });
  const [discrepanciesExist, setDiscrepanciesExist] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalItemId, setNotesModalItemId] = useState<number | null>(null);
  const [notesModalText, setNotesModalText] = useState<string>('');
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [creditReviewEntries, setCreditReviewEntries] = useState<{ [key: number]: { quantity: number; uom: string; creditNote: string } }>({});
  const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false);
  const [creditNoteModalItemId, setCreditNoteModalItemId] = useState<number | null>(null);
  const [creditNoteModalText, setCreditNoteModalText] = useState<string>('');
  const [reviewCompleteModalOpen, setReviewCompleteModalOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [creditValues, setCreditValues] = useState<{ [key: number]: number }>({});
  const [originalCreditEntries, setOriginalCreditEntries] = useState<{ [key: number]: { quantity: number; uom: string } }>({});
  const [lineCalcLoadingMap, setLineCalcLoadingMap] = useState<Record<number, boolean>>({});

  const [page, setPage] = useState(0);
  const pageSize = 10;
  const creditRequestStatus = deliveryItems.length > 0 ? getCreditRequestValue(deliveryItems[0]?.OrderCreditRequestStatus.toString()) : null;

  const inReview = creditRequestStatus === 'Review';
  const complete = creditRequestStatus === 'Completed';
  const userIsReviewer = hasPermissionTo(['CreditRequestsReview']);
  const hasPassedClaimPeriod = deliveryItems.length > 0 ? deliveryItems[0]?.HasPassedClaimPeriod && inReview: false;

  useEffect(() => { fetchCreditRequests();  getCurrencyCodeConfiguration(); }, [selectedStore, id]);

  const fetchCreditRequests = async () => {
    if (!selectedStore || !id) { setLoading(false); return; }
    if (!hasPermissionTo(['CreditRequestsView'])) { setLoading(false); return; }
    setLoading(true);
    try {
      const result = await client.getCreditRequests(selectedStore.storeNumber ?? '0', id);
      if (result?.data) {
        setDeliveryItems(result.data);
        setPage(0);
        setCreditEntries({});
        const initReview: { [key: number]: { quantity: number; uom: string; creditNote: string } } = {};
        const initOriginal: { [key: number]: { quantity: number; uom: string } } = {};
        result.data.forEach((it: CreditRequestItemModel) => {
          const idKey = it.WebOrderItemCreditRequestID;
          initReview[idKey] = {
            quantity: it.ClaimQuantity ?? 0,
            uom: it.ShippedUom ?? 'CS',
            creditNote: (it as any).ConfirmedCreditNote ?? (it as any).CreditNote ?? '',
          };
          initOriginal[idKey] = {
            quantity: it.ClaimQuantity ?? 0,
            uom: it.ShippedUom ?? 'CS',
          };
        });
        setCreditReviewEntries(initReview);
        setOriginalCreditEntries(initOriginal);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditChange = (itemId: number, field: keyof CreditRequestEntry, value: any) => {
    setCreditEntries(prev => {
      if (field === 'notes' && (value === '' || value == null)) {
        const item = deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId);
        if (item && item.Notes) {
          return { ...prev, [itemId]: { ...prev[itemId], notes: item.Notes } };
        }
      }
      return { ...prev, [itemId]: { ...prev[itemId], [field]: value } };
    });
  };

  const getFilePayload = async (files: File[]) => {
    const filePromises = files.map(file => {
      return new Promise<{ FileName: string; FileType: string; FileData: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve({
            FileName: file.name,
            FileType: file.type,
            FileData: base64,
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });
    return Promise.all(filePromises);
  };

  const handleDownloadFiles = async (creditRequestId: number, productCode: number) => {
    try {
      const blob = await client.downloadCreditRequestFiles(selectedStore?.storeNumber ?? '0', creditRequestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CreditRequest_${id}_${productCode}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.downloadFilesError') + ': ' + error, 'error');
    }
  };

  const handleSaveCreditRequest = async () => {
    if (!selectedStore || !id) return;
    if (!hasPermissionTo(['CreditRequestsSave'])) return;
    setLoading(true);
    try {
      const payload: SaveCreditRequestItemModel[] = await Promise.all(
        Object.entries(creditEntries).map(async ([id, entry]) => {
          const item = deliveryItems.find(i => i.WebOrderItemCreditRequestID === Number(id));
          const notesToSend = (entry.notes !== undefined && entry.notes !== null && entry.notes !== '')
            ? entry.notes
            : (item && item.Notes ? item.Notes : '');
          return {
            WebOrderItemCreditRequestID: Number(id),
            Reason: entry.reason as CreditRequestReasonEnum,
            Notes: notesToSend,
            Files: entry.files ? await getFilePayload(entry.files) : [],
          };
        }),
      );

      const result = await client.saveCreditRequest(selectedStore.storeNumber ?? '0', payload);
      if (result.success) {
        setCreditEntries({});
        handleSnackbarOpen(t('deliveriesList.creditRequestSaved'), 'success');
        await fetchCreditRequests();
      } else {
        handleSnackbarOpen(result.message || t('deliveriesList.creditRequestSaveError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.creditRequestSaveError') + ': ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCreditRequest = async () => {
    if (!selectedStore || !id) return;
    if (!hasPermissionTo(['CreditRequestsSubmit'])) return;
    setLoading(true);
    try {
      const result = await client.submitCreditRequest(selectedStore.storeNumber ?? '0', id);
      if (result.success) {
        navigate(location.pathname, { replace: true, state: { successMessage: t('deliveriesList.submittedSuccess') } });
        await fetchCreditRequests();
      } else {
        handleSnackbarOpen(result.message || t('deliveriesList.creditRequestSaveError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.creditRequestSaveError') + ': ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReasonChange = (itemId: number, value: CreditRequestReasonEnum) => {
    handleCreditChange(itemId, 'reason', value);
  };

  const handleNotesChange = (itemId: number, value: string) => {
    handleCreditChange(itemId, 'notes', value);
  };

  const handleFileUpload = (itemId: number, files: FileList | null) => {
    if (!files) return;
    const item = deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId);

    const allFiles = Array.from(files);
    const imageFiles = allFiles.filter(f =>
      (f.type && f.type.startsWith('image/')) ||
      /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(f.name),
    );
    const nonImageRejectedCount = allFiles.length - imageFiles.length;

    const existingLocal = creditEntries[itemId]?.files ?? [];
    const existingLocalNames = new Set(existingLocal.map(f => f.name.toLowerCase()));
    const existingServerNames = new Set((item?.FileNames ?? []).map(name => name.toLowerCase()));

    const newFiles: File[] = [];
    let duplicateCount = 0;

    imageFiles.forEach(f => {
      const lower = f.name.toLowerCase();
      if (
        existingLocalNames.has(lower) ||
        existingServerNames.has(lower) ||
        newFiles.some(nf => nf.name.toLowerCase() === lower)
      ) {
        duplicateCount += 1;
      } else {
        newFiles.push(f);
      }
    });

    if (nonImageRejectedCount > 0) {
      handleSnackbarOpen(t('deliveriesList.nonImageFilesRejected'), 'warning');
    }
    if (duplicateCount > 0) {
      handleSnackbarOpen(t('deliveriesList.duplicateFileNameRejected'), 'warning');
    }
    if (newFiles.length === 0) return;

    handleCreditChange(itemId, 'files', [...existingLocal, ...newFiles]);
  };

  useEffect(() => {
    const allValid = deliveryItems.every(item => {
      const entry = creditEntries[item.WebOrderItemCreditRequestID];
      const reason = entry?.reason ?? item.ClaimReason;
      if (!reason || reason <= 0) return false;
      if (reason === CreditRequestReasonEnum.Damaged) {
        const fileCount =
          (entry?.files?.length ?? 0) +
          (item.FileNames ? item.FileNames.length : 0);
        if (fileCount < 1) return false;
      }
      return true;
    });
    setDiscrepanciesExist(allValid);
  }, [creditEntries, deliveryItems]);

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  const handleNotesIconClick = useCallback((itemId: number) => {
    const item = deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId);
    const notes =
      creditEntries[itemId]?.notes ??
      (item && item.Notes ? item.Notes : '');
    setNotesModalItemId(itemId);
    setNotesModalText(notes);
    setNotesModalOpen(true);
  }, [creditEntries, deliveryItems]);

  const handleNotesModalClose = () => {
    setNotesModalOpen(false);
    setNotesModalItemId(null);
    setNotesModalText('');
  };

  const handleNotesModalSave = () => {
    if (notesModalItemId !== null) {
      handleNotesChange(notesModalItemId, notesModalText);
    }
    handleNotesModalClose();
  };

  const handleOpenSubmitModal = async () => {
    if (Object.keys(creditEntries).length > 0) {
      await handleSaveCreditRequest();
    }
    setSubmitModalOpen(true);
  };

  const handleCloseSubmitModal = () => setSubmitModalOpen(false);

  const handleConfirmSubmit = async () => {
    setSubmitModalOpen(false);
    setLoading(true);
    try {
      await handleSendCreditRequest();
      handleSnackbarOpen(t('deliveriesList.submittedSuccess'), 'success');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditReviewChange = (
    itemId: number,
    field: 'quantity' | 'uom' | 'creditNote',
    value: any,
  ) => {
    setCreditReviewEntries(prev => {
      const current = prev[itemId] ?? {
        quantity: deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId)?.ClaimQuantity ?? 0,
        uom: deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId)?.ShippedUom ?? 'CS',
        creditNote: (deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId) as any)?.ConfirmedCreditNote ?? '',
      };
      const newVal = field === 'quantity' ? Number(value) : value;
      return { ...prev, [itemId]: { ...current, [field]: newVal } };
    });
  };

  const handleCreditNoteIconClick = (itemId: number) => {
    if (!userIsReviewer) {
      const item = deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId);
      setCreditNoteModalItemId(null);
      setCreditNoteModalText((item as any)?.ConfirmedCreditNote ?? (item as any)?.CreditNote ?? '');
      setCreditNoteModalOpen(true);
      return;
    }
    setCreditNoteModalItemId(itemId);
    setCreditNoteModalText(creditReviewEntries[itemId]?.creditNote ?? '');
    setCreditNoteModalOpen(true);
  };

  const handleCreditNoteModalClose = () => {
    setCreditNoteModalOpen(false);
    setCreditNoteModalItemId(null);
    setCreditNoteModalText('');
  };

  const handleCreditNoteModalSave = () => {
    if (!userIsReviewer) {
      handleCreditNoteModalClose();
      return;
    }
    if (creditNoteModalItemId !== null) {
      handleCreditReviewChange(creditNoteModalItemId, 'creditNote', creditNoteModalText);
    }
    handleCreditNoteModalClose();
  };

  const getCreditLineStatus = (
    item: CreditRequestItemModel,
    creditValue: number,
  ) => {
    const claimValue = item.ClaimValue ?? 0;
    if (creditValue === 0) {
      return {
        icon: <Cancel sx={{ color: 'error.main', verticalAlign: 'middle' }}
          fontSize="small" />,
        label: 'Rejected',
      };
    }
    if (creditValue === claimValue) {
      return {
        icon: <CheckCircle sx={{ color: 'success.main', verticalAlign: 'middle' }}
          fontSize="small" />,
        label: 'Approved',
      };
    }
    if (creditValue > 0 && creditValue < claimValue) {
      return {
        icon: <Edit sx={{ color: 'warning.main', verticalAlign: 'middle' }}
          fontSize="small" />,
        label: 'Modified',
      };
    }
    return { label: '', icon: null };
  };

  const handleCompleteReview = async () => {
    setReviewSubmitting(true);
    const payload = deliveryItems.map(item => {
      const entry = creditReviewEntries[item.WebOrderItemCreditRequestID] ?? {
        quantity: 0,
        uom: item.ShippedUom ?? 'CS',
        creditNote: '',
      };
      return {
        WebOrderItemCreditRequestID: item.WebOrderItemCreditRequestID,
        ItemId: item.ItemId,
        CreditQuantity: entry.quantity,
        CreditUom: entry.uom,
        CreditNote: entry.creditNote,
      };
    });
    try {
      const res = await client.completeCreditReview(selectedStore?.storeNumber ?? '0', id ?? '0', payload);
      if (res.success) {
        const msg = t('deliveriesList.reviewCompletedSuccess');
        handleSnackbarOpen(msg, 'success');
        setReviewCompleteModalOpen(false);
        navigate('/deliveries', { state: { successMessage: msg } });
      } else {
        handleSnackbarOpen(t('deliveriesList.reviewCompletedError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.reviewCompletedError') + ' ' + error, 'error');
    } finally {
      setReviewSubmitting(false);
    }
  };
  const handleCreditReviewBlur = async (itemId: number, productCode: number) => {
    const entry = creditReviewEntries[itemId];
    const item = deliveryItems.find(i => i.WebOrderItemCreditRequestID === itemId);
    if (!entry || !item) return;

    if (lineCalcLoadingMap[itemId]) return;

    setLineCalcLoadingMap(prev => ({ ...prev, [itemId]: true }));

    if (!originalCreditEntries[itemId]) {
      setOriginalCreditEntries(prev => ({
        ...prev,
        [itemId]: {
          quantity: item.ClaimQuantity ?? 0,
          uom: item.ShippedUom ?? 'CS',
        },
      }));
    }

    try {
      const result = await client.getCreditValueForLine(selectedStore?.storeNumber ?? '0', {
        ItemId: productCode,
        CreditQuantity: entry.quantity,
        CreditUom: entry.uom,
        OrderNumber: id ?? '0',
      });

      if (result.success && typeof result.data?.CreditValue === 'number') {
        const returnedRaw = result.data.CreditValue;
        const newCreditValueUnits = returnedRaw / 100;

        const creditedValue = item.ClaimValue ? item.ClaimValue / 100 : 0;

        if (newCreditValueUnits > creditedValue) {
          handleSnackbarOpen(t('deliveriesList.creditValueExceedsError'), 'warning');

          const original = originalCreditEntries[itemId] || {
            quantity: item.ClaimQuantity ?? 0,
            uom: item.ShippedUom ?? 'CS',
          };

          handleCreditReviewChange(itemId, 'quantity', original.quantity);
          handleCreditReviewChange(itemId, 'uom', original.uom);

          const revertResult = await client.getCreditValueForLine(
            selectedStore?.storeNumber ?? '0',
            {
              ItemId: itemId,
              CreditQuantity: original.quantity,
              CreditUom: original.uom,
              OrderNumber: id ?? '0',
            },
          );

          if (revertResult.success && typeof revertResult.data?.CreditValue === 'number') {
            setCreditValues(prev => ({
              ...prev,
              [itemId]: revertResult.data.CreditValue / 100,
            }));
          } else {
            setCreditValues(prev => ({ ...prev, [itemId]: creditedValue }));
          }
        }
        else {
          setCreditValues(prev => ({ ...prev, [itemId]: newCreditValueUnits }));
        }
      } else {
        const fallback = item.ClaimValue && item.ClaimQuantity
          ? (item.ClaimValue / item.ClaimQuantity) * (entry.quantity ?? 0) / 100
          : 0;
        setCreditValues(prev => ({ ...prev, [itemId]: fallback }));
      }
    } catch (error) {
      handleSnackbarOpen(t('deliveriesList.creditValueError') + ' ' + error, 'error');
    } finally {
      setLineCalcLoadingMap(prev => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  const anyLineCalculating = Object.values(lineCalcLoadingMap).some(v => v === true);

  useEffect(() => {
    const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;
    if (successMessage) {
      handleSnackbarOpen(successMessage, 'success');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const pagedDeliveryItems = deliveryItems.slice(page * pageSize, (page + 1) * pageSize);
  const maxPages = Math.ceil(deliveryItems.length / pageSize) || 1;

  return (
    <PageArea>
      {userIsReviewer ? (
        <Grid item
          xs={12}
          mb={10}>
          <Box mb={2}>
            <Breadcrumbs aria-label="breadcrumb"
              sx={{ fontSize: 14 }}>
              <Link
                underline="hover"
                color="inherit"
                onClick={() => navigate('/deliveries')}
                sx={{ cursor: 'pointer' }}
              >
                {t('deliveriesList.breadcrumbDeliveries')}
              </Link>
              <Link
                underline="hover"
                color="inherit"
                onClick={() => navigate(`/deliveries/${id}`)}
                sx={{ cursor: 'pointer' }}
              >
                {t('deliveriesList.breadcrumbDeliveryDetail')}
              </Link>
              <Typography color="text.primary">
                {t('deliveriesList.breadcrumbCreditRequestReview')}
              </Typography>
            </Breadcrumbs>
          </Box>
        </Grid>
      ) : (
        <Grid item
          xs={12}
          mb={10}>
          <BackButton handleClick={() => navigate('/deliveries')}
            title={t('deliveriesList.exit')}
            isLink={false} />
        </Grid>
      )}
      <Grid container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}>
        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PageTitle>
              {userIsReviewer
                ? `${t('deliveriesList.creditRequestReview')} - ${id} ${deliveryItems && deliveryItems.map(x => x.OrderType)[0] ? '- ' + deliveryItems.map(x => x.OrderType)[0] : ''}`
                : `${t('deliveriesList.creditRequest')} - ${id} ${deliveryItems && deliveryItems.map(x => x.OrderType)[0] ? '- ' + deliveryItems.map(x => x.OrderType)[0] : ''}`}
            </PageTitle>
            {creditRequestStatus && (
              <OrderStatusChip orderStatus={creditRequestStatus} />
            )}
          </Box>
        </Grid>
        <Grid item>
          {userIsReviewer ? (
            <Button
              variant="primary"
              size="lg"
              disabled={reviewSubmitting || anyLineCalculating || complete}
              onClick={() => setReviewCompleteModalOpen(true)}
            >
              {t('deliveriesList.completeReview')}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {hasPermissionTo(['CreditRequestsSave']) &&
                <Button
                  variant="primary"
                  size="lg"
                  disabled={loading || inReview || complete}
                  onClick={handleSaveCreditRequest}
                  sx={{ minWidth: 200 }}
                >
                  {t('deliveriesList.saveProgress')}
                </Button>
              }
              {hasPermissionTo(['CreditRequestsSubmit']) &&
                <Button
                  variant="secondary"
                  size="lg"
                  disabled={loading || !discrepanciesExist || inReview || complete}
                  onClick={handleOpenSubmitModal}
                  sx={{ minWidth: 200 }}
                >
                  {t('deliveriesList.submit')}
                </Button>
              }
            </Box>
          )}
        </Grid>
      </Grid>

      <Box mb={2}
        sx={{ fontWeight: 500, fontSize: 16 }}>
        {userIsReviewer
          ? t('deliveriesList.creditRequestReviewSubtitle')
          : t('deliveriesList.deliveriesSubtitle')}
      </Box>

      {userIsReviewer ? (
        <>
          <TableContainer>
            <Table size="small"
              sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('deliveriesList.productId')}</TableCell>
                  <TableCell>{t('deliveriesList.description')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.shippedQuantity')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.receivedQuantity')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.claimQuantity')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.claimValue')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.reason')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.files')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.claimNotes')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.creditQuantity')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.creditUom')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.creditValue')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.creditNote')}</TableCell>
                  <TableCell align="center">{t('deliveriesList.creditStatus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedDeliveryItems.map(item => {
                  const idKey = item.WebOrderItemCreditRequestID;
                  const claimUom = item.ShippedUom ?? 'CS';
                  const creditEntry = creditReviewEntries[idKey] ?? {
                    quantity: item.ClaimQuantity ?? 0,
                    uom: claimUom,
                    creditNote: (item as any).ConfirmedCreditNote ?? '',
                  };

                  const displayedCreditValue =
                    typeof creditValues[idKey] === 'number'
                      ? creditValues[idKey]
                      : ((item.ClaimValue && item.ClaimQuantity)
                        ? (item.ClaimValue / item.ClaimQuantity) * (creditEntry.quantity ?? 0) / 100
                        : 0);

                  const statusCreditValue =
                    typeof creditValues[idKey] === 'number'
                      ? creditValues[idKey] * 100
                      : ((item.ClaimValue && item.ClaimQuantity)
                        ? item.ClaimValue
                        : 0);

                  const statusObj = getCreditLineStatus(item, statusCreditValue);

                  return (
                    <TableRow key={idKey}>
                      <TableCell><TextWithOverflowTooltip text={item.ItemId.toString()} /></TableCell>
                      <TableCell><TextWithOverflowTooltip text={item.Name} /></TableCell>
                      <TableCell align="center">{item.ShippedQuantity} {item.ShippedUom}</TableCell>
                      <TableCell align="center">{item.ReceivedQuantity} {item.ReceivedUom}</TableCell>
                      <TableCell align="center">{item.ClaimQuantity} {claimUom}</TableCell>
                      <TableCell align="center">{applyRoundAndFormat(item.ClaimValue ? item.ClaimValue / 100 : 0, currencyCode)}</TableCell>
                      <TableCell align="center">{t(`deliveriesList.reason${item.ClaimReason === 1 ? 'Damaged' : item.ClaimReason === 2 ? 'Short' : 'Other'}`)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleDownloadFiles(idKey, item.ItemId)}
                          disabled={!item.FileNames || item.FileNames.length === 0}
                        >
                          <Download />
                        </IconButton>
                        {item.FileNames && item.FileNames.length > 0 && (
                          <Box ml={1}>
                            {item.FileNames.length} {t('deliveriesList.files')}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleNotesIconClick(idKey)}
                          size="small"
                        >
                          <Note />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          disabled={complete}
                          inputProps={{ min: 0 }}
                          value={creditEntry.quantity}
                          onChange={e => {
                            let val = Number(e.target.value);
                            if (Number.isNaN(val)) val = 0;
                            if (val < 0) val = 0;
                            handleCreditReviewChange(idKey, 'quantity', val);
                          }}
                          onBlur={() => handleCreditReviewBlur(idKey, item.ItemId)}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          select
                          size="small"
                          disabled={complete}
                          value={creditEntry.uom}
                          onChange={e => handleCreditReviewChange(idKey, 'uom', e.target.value)}
                          onBlur={() => handleCreditReviewBlur(idKey, item.ItemId)}
                          sx={{ width: 80 }}
                        >
                          <MenuItem value={claimUom}>{claimUom}</MenuItem>
                          {UOM_OPTIONS.filter(u => u.value !== claimUom).map(uom => (
                            <MenuItem key={uom.value}
                              value={uom.value}>{uom.label}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="center">
                        {lineCalcLoadingMap[idKey] ? (
                          <CircularProgress size={20} />
                        ) : (
                          applyRoundAndFormat(displayedCreditValue, currencyCode)
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleCreditNoteIconClick(idKey)}
                          size="small"
                        >
                          <Note />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        {statusObj.icon}
                        {statusObj.label && <span style={{ marginLeft: 6 }}>{statusObj.label}</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={5} />
                  <TableCell align="center"
                    sx={{ fontWeight: 'bold' }}>
                    {applyRoundAndFormat(
                      deliveryItems.reduce((sum, item) =>
                        sum + (item.ClaimValue != null && item.ClaimValue > 0 ? item.ClaimValue / 100 : 0), 0),
                      currencyCode,
                    )}
                  </TableCell>
                  <TableCell colSpan={5} />
                  <TableCell align="center"
                    sx={{ fontWeight: 'bold' }}>
                    {anyLineCalculating ? (
                      <CircularProgress size={20} />
                    ) : (
                      applyRoundAndFormat((deliveryItems.reduce((sum, item) => {
                        const creditVal = creditValues[item.WebOrderItemCreditRequestID];
                        if (typeof creditVal === 'number') return sum + creditVal;
                        const creditEntry = creditReviewEntries[item.WebOrderItemCreditRequestID] ?? { quantity: item.ClaimQuantity ?? 0, uom: item.ShippedUom ?? 'CS', note: '' };
                        return sum + ((item.ClaimValue && item.ClaimQuantity)
                          ? (item.ClaimValue / item.ClaimQuantity) * (creditEntry.quantity ?? 0) / 100
                          : 0);
                      }, 0)), currencyCode)

                    )}
                  </TableCell>

                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
            />
          </TableContainer>

          <Dialog open={notesModalOpen}
            onClose={handleNotesModalClose}>
            <DialogTitle>{t('deliveriesList.claimNotes')}</DialogTitle>
            <DialogContent>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={notesModalText}
                onChange={e => setNotesModalText(e.target.value)}
                disabled={inReview || complete}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleNotesModalClose}>{t('deliveriesList.close')}</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={reviewCompleteModalOpen}
            onClose={() => setReviewCompleteModalOpen(false)}>
            <DialogTitle>{t('deliveriesList.completeReviewConfirmTitle')}</DialogTitle>
            <DialogContent>
              <Box>{t('deliveriesList.completeReviewConfirmText')}</Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReviewCompleteModalOpen(false)}
                variant="bad">{t('deliveriesList.cancel')}</Button>
              <Button onClick={handleCompleteReview}
                variant="primary"
                disabled={reviewSubmitting}>
                {t('deliveriesList.completeReview')}
              </Button>
            </DialogActions>
          </Dialog>

        </>
      ) : (
        <>
          {loading ? <LoadingWheel /> :
            <TableContainer>
              <Table size="small"
                sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('deliveriesList.productId')}</TableCell>
                    <TableCell>{t('deliveriesList.name')}</TableCell>
                    <TableCell align='center'>{t('deliveriesList.shippedQuantity')}</TableCell>
                    <TableCell align='center'>{t('deliveriesList.receivedQuantity')}</TableCell>
                    <TableCell align='center'>{t('deliveriesList.creditQuantity')}</TableCell>
                    <TableCell align='center'>{t('deliveriesList.creditAmount')}</TableCell>
                    <TableCell align='center'>{t('deliveriesList.reason')}</TableCell>
                    <TableCell align='center'>{t('deliveriesList.files')}</TableCell>
                    <TableCell>{t('deliveriesList.notes')}</TableCell>
                    {(inReview && userIsReviewer) || complete &&
                      <><TableCell align='center'>{t('deliveriesList.creditQuantity')}</TableCell>
                        <TableCell align='center'>{t('deliveriesList.creditUom')}</TableCell>
                        <TableCell align='center'>{t('deliveriesList.creditValue')}</TableCell>
                        <TableCell align='center'>{t('deliveriesList.creditStatus')}</TableCell>
                        <TableCell align='center'>{t('deliveriesList.creditNote')}</TableCell>
                      </>
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDeliveryItems.map(item => {
                    const uploadedFilesCount = creditEntries[item.WebOrderItemCreditRequestID]?.files?.length ?? 0;
                    const existingFilesCount = item.FileNames ? item.FileNames.length : 0;
                    const showCombined = uploadedFilesCount > 0 && existingFilesCount > 0;
                    const displayFileCount = uploadedFilesCount > 0 ? uploadedFilesCount : existingFilesCount;

                    const statusObj = getCreditLineStatus(item, item.ConfirmedCreditValue ?? 0);
                    return (
                      <TableRow key={item.WebOrderItemCreditRequestID}>
                        <TableCell><TextWithOverflowTooltip text={item.ItemId.toString()} /></TableCell>
                        <TableCell><TextWithOverflowTooltip text={item.Name} /></TableCell>
                        <TableCell align='center'>{item.ShippedQuantity} {item.ShippedUom}</TableCell>
                        <TableCell align='center'>{item.ReceivedQuantity} {item.ReceivedUom}</TableCell>
                        <TableCell align='center'>{item.ClaimQuantity} {item.ShippedUom}</TableCell>
                        <TableCell align='center'>
                          {applyRoundAndFormat(
                            item.ClaimValue != null && item.ClaimValue > 0 ? item.ClaimValue / 100 : 0,
                            currencyCode,
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <TextField
                            select
                            size="small"
                            disabled={inReview || complete || userIsReviewer || hasPassedClaimPeriod}
                            value={
                              creditEntries[item.WebOrderItemCreditRequestID]?.reason ?? item.ClaimReason ?? CreditRequestReasonEnum.None
                            }
                            onChange={e =>
                              handleReasonChange(
                                item.WebOrderItemCreditRequestID,
                                Number(e.target.value) as CreditRequestReasonEnum,
                              )
                            }
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value={CreditRequestReasonEnum.Damaged}>{t('deliveriesList.reasonDamaged')}</MenuItem>
                            <MenuItem value={CreditRequestReasonEnum.Short}>{t('deliveriesList.reasonShort')}</MenuItem>
                            <MenuItem value={CreditRequestReasonEnum.Other}>{t('deliveriesList.reasonOther')}</MenuItem>
                          </TextField>
                        </TableCell>

                        <TableCell align="center">
                          <Grid container
                            justifyContent="center"
                            alignItems="center"
                            gap={1}>
                            {!complete &&
                            <><input
                              aria-hidden
                              type="file"
                              multiple
                              accept="image/*"
                              style={{ display: 'none' }}
                              id={`file-upload-${item.WebOrderItemCreditRequestID}`}
                              onChange={e => handleFileUpload(item.WebOrderItemCreditRequestID, e.target.files)}
                              disabled={inReview || complete || userIsReviewer || hasPassedClaimPeriod} />
                            <IconButton
                              disabled={inReview || complete || userIsReviewer || hasPassedClaimPeriod}
                              color="primary"
                              onClick={() => {
                                const input = document.getElementById(`file-upload-${item.WebOrderItemCreditRequestID}`) as HTMLInputElement;
                                input?.click();
                              } }
                            >
                              <UploadFile />
                            </IconButton></>
                            }

                            <IconButton
                              color="primary"
                              onClick={() => handleDownloadFiles(item.WebOrderItemCreditRequestID, item.ItemId)}
                              disabled={existingFilesCount === 0}
                            >
                              <Download />
                            </IconButton>

                            {(uploadedFilesCount > 0 || existingFilesCount > 0) && !complete && (
                              <Box ml={1}>
                                {showCombined
                                  ? `${uploadedFilesCount} new, ${existingFilesCount} existing`
                                  : `${displayFileCount} file${displayFileCount === 1 ? '' : 's'}`}
                              </Box>
                            )}
                          </Grid>
                        </TableCell>

                        <TableCell align="left">
                          <IconButton
                            color="primary"
                            onClick={() => handleNotesIconClick(item.WebOrderItemCreditRequestID)}
                            size="small"
                            disabled={hasPassedClaimPeriod}
                          >
                            <Note />
                          </IconButton>
                        </TableCell>
                        {(inReview && userIsReviewer) || complete &&
                          <><TableCell align="center">
                            <TextField
                              size="small"
                              disabled
                              value={(item as any).ConfirmedCreditQuantity != null ? (item as any).ConfirmedCreditQuantity : item.ClaimQuantity ?? 0}
                              sx={{ width: 80 }} />
                          </TableCell><TableCell align="center">
                            <TextField
                              size="small"
                              disabled
                              value={(item as any).ConfirmedCreditUom != null ? (item as any).ConfirmedCreditUom : item.ShippedUom ?? 'CS'}
                              sx={{ width: 80 }} />
                          </TableCell><TableCell align="center">
                            {applyRoundAndFormat(
                              (item.ConfirmedCreditValue ?? item.ClaimValue ?? 0) / 100,
                              currencyCode,
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {statusObj.icon}
                            {statusObj.label && <span style={{ marginLeft: 6 }}>{statusObj.label}</span>}
                          </TableCell>
                          <TableCell align="center">
                            {complete ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifySelf: 'center', gap: 0.5 }}>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleCreditNoteIconClick(item.WebOrderItemCreditRequestID)}
                                >
                                  <Note />
                                </IconButton>
                                <TextWithOverflowTooltip text={(item as any).ConfirmedCreditNote ?? (item as any).CreditNote ?? ''} />
                              </Box>
                            ) : null}
                          </TableCell>
                          </>
                        }
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={5} />
                    <TableCell align="center"
                      sx={{ fontWeight: 'bold' }}>
                      {(() => {
                        const totalClaim = deliveryItems.reduce((sum, item) =>
                          sum + (item.ClaimValue != null && item.ClaimValue > 0 ? item.ClaimValue / 100 : 0), 0);
                        return applyRoundAndFormat(totalClaim, currencyCode);
                      })()}
                    </TableCell>
                    <TableCell colSpan={3} />
                    {complete && (
                      <>
                        <TableCell colSpan={2} />
                        <TableCell align="center"
                          sx={{ fontWeight: 'bold' }}>
                          {(() => {
                            const totalCredit = deliveryItems.reduce((sum, item) => {
                              const val = (item as any).ConfirmedCreditValue != null
                                ? (item as any).ConfirmedCreditValue / 100
                                : (item.ClaimValue != null && item.ClaimValue > 0 ? item.ClaimValue / 100 : 0);
                              return sum + val;
                            }, 0);
                            return applyRoundAndFormat(totalCredit, currencyCode);
                          })()}
                        </TableCell>
                        <TableCell colSpan={2} />
                      </>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
              <CustomPagination
                page={page}
                setPage={setPage}
                maxPages={maxPages}
                breakpointForChangeDisplay={120000}
              />
            </TableContainer>
          }

          <Dialog open={notesModalOpen}
            onClose={handleNotesModalClose}>
            <DialogTitle>{t('deliveriesList.notes')}</DialogTitle>
            <DialogContent>
              <TextField
                multiline
                disabled={inReview || complete || userIsReviewer || hasPassedClaimPeriod}
                rows={4}
                fullWidth
                value={notesModalText}
                onChange={e => setNotesModalText(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleNotesModalClose}>{t('deliveriesList.close')}</Button>
              {!inReview && !complete && !hasPassedClaimPeriod && !userIsReviewer &&
                <Button onClick={handleNotesModalSave}>{t('deliveriesList.confirm')}</Button>
              }
            </DialogActions>
          </Dialog>

          <Dialog open={submitModalOpen}
            onClose={handleCloseSubmitModal}>
            <DialogTitle>{t('deliveriesList.submitConfirmTitle')}</DialogTitle>
            <DialogContent>
              <Box>
                {t('deliveriesList.submitConfirmText')}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSubmitModal}
                variant="bad">{t('deliveriesList.cancel')}</Button>
              <Button onClick={handleConfirmSubmit}
                variant="primary">{t('deliveriesList.submit')}</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Dialog open={creditNoteModalOpen}
        onClose={handleCreditNoteModalClose}>
        <DialogTitle>{t('deliveriesList.creditNote')}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            fullWidth
            disabled={!userIsReviewer || complete}
            value={creditNoteModalText}
            onChange={e => setCreditNoteModalText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreditNoteModalClose}>{t('deliveriesList.close')}</Button>
          {userIsReviewer && !complete && (
            <Button onClick={handleCreditNoteModalSave}>{t('deliveriesList.confirm')}</Button>
          )}
        </DialogActions>
      </Dialog>

      <MessagesSnackbar open={snackbar.open}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={3000} />
    </PageArea>
  );
};

export default CreditRequestsOfd;
